import { AppShellMain, Affix, Transition, ActionIcon, Box, AppShell, Button, Flex, Group, Text } from "@mantine/core"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { sortVideos, urlSafeBase64Decode, urlSafeBase64Encode } from "../utils/utils"
import { useEffect, useMemo, useState, useCallback, useContext } from "react"
import FocusedVideoDisplay from "../components/VideoDisplay/FocusedVideoDisplay"
import { GameSubQuery, PlayerSubQuery, Season, SeasonType, Stat, VideoEvent } from "../client"
import { getBatchPlayerPBPVideos, getPlayerPBPVideos } from "../api/Players"
import { getClip, getGamePBPVideos, getBatchGamePBPVideos } from "../api/Games"
import { LEAGUE } from "../models/NBA"
import { useLeague } from "../context/LeagueContext"
import { IconArrowUp } from "@tabler/icons-react"
import { NBAInsightsHeader } from "../components/Header/Header"
import { useHeadroom } from "@mantine/hooks"
import PageLoader from "../components/Common/PageLoader"
import { decodeQueryStack, StackedGameQuery, StackedPlayerQuery } from "../models/QueryStack"
import { VideoSearchRanges } from "../models/SearchData"
import { StatSchema } from "../client/schemas.gen"
import { resolveTargetPlayerId } from "../utils/player"
import { NBAInsightsContext } from "../context/global-state"
import { parseLeague } from "../utils/league"

interface VideoSearchQueryData {
  type?: string
  league?: LEAGUE | string
  player?: string
  lastN?: string
  stats?: string
  seasons?: string
  seasonType?: SeasonType
  opponent?: string
  dateFrom?: string
  dateTo?: string
  game?: string
  players?: string
  periods?: string
  q?: string
}

const Video: React.FC = () => {
  // Layout info
  const pinned = useHeadroom({ fixedAt: 120 });

  const { searchCache, mergeSearchResults } = useLeague()
  const { games, nbaPlayers, wnbaPlayers } = useContext(NBAInsightsContext)

  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // scroll to top
  const [scrollIsVisible, setScrollIsVisible] = useState(false);

  // video state
  const limit = 15
  const [video, setVideo] = useState<VideoEvent>()
  const [clipLoading, setClipLoading] = useState<boolean>(true)
  const [clipError, setClipError] = useState<boolean>(false)

  // from state
  const [activeSearch, setActiveSearch] = useState<boolean>(false)

  // get selected video info
  const { hash } = useParams();
  const [ gameId, eventNumber ] = urlSafeBase64Decode(hash!).split(':')

  const from = searchParams.get('from')
  const fromLeague = useMemo(() => {
    if (!from) return null
    try {
      const data = Object.fromEntries(new URLSearchParams(decodeURIComponent(from)))
      return parseLeague(data.league)
    } catch {
      return null
    }
  }, [from])

  const sortParam = searchParams.get('sort')
  const isDesc = sortParam !== 'asc'

  const searchSnapshot = fromLeague ? searchCache[fromLeague] : null
  const displayVideos = useMemo(
    () => (searchSnapshot ? sortVideos(searchSnapshot.videos, isDesc) : []),
    [searchSnapshot, isDesc]
  )
  const total = searchSnapshot ? searchSnapshot.total : 0
  const hasMore = searchSnapshot ? searchSnapshot.hasMore : false
  const cursor = searchSnapshot ? searchSnapshot.cursor : null

  const resolvedTargetPlayerId = useMemo(() => {
    if (!video || !from) return undefined
    try {
      const data = Object.fromEntries(new URLSearchParams(decodeURIComponent(from))) as VideoSearchQueryData
      if (data.q) {
        const searchLeague = parseLeague(data.league)
        const activePlayers = searchLeague === LEAGUE.WNBA ? wnbaPlayers : nbaPlayers
        const decodedQueries = decodeQueryStack(data.q, searchLeague, activePlayers, games)
        const ids: string[] = []
        const allStats: string[] = []
        decodedQueries.forEach((q) => {
          if (q.type === "player" && q.player?.id) {
            ids.push(String(q.player.id))
          } else if (q.type === "game" && q.players) {
            q.players.forEach((p) => ids.push(String(p.id)))
          }
          if (q.categories) {
            q.categories.forEach((s) => allStats.push(s))
          }
        })
        return resolveTargetPlayerId(video, ids, allStats)
      } else if (data.type === "player") {
        return data.player
      } else if (data.type === "game") {
        const pIds = data.players ? data.players.split(',').map((s) => s.trim()) : data.player ? [data.player] : []
        const statsList = data.stats ? data.stats.split(',').map((s) => s.trim()) : []
        return resolveTargetPlayerId(video, pIds, statsList)
      }
      return undefined
    } catch {
      return undefined
    }
  }, [video, from, nbaPlayers, wnbaPlayers, games])

  const fetchData = useCallback(async (data: VideoSearchQueryData, currentCursor: string | null) => {
    let result = null
    const searchLeague = parseLeague(data.league)

    const order = isDesc ? 'desc' : 'asc'

    if (data.q) {
      const activePlayers = searchLeague === LEAGUE.WNBA ? wnbaPlayers : nbaPlayers
      const decodedQueries = decodeQueryStack(data.q, searchLeague, activePlayers, games)

      if (decodedQueries.length > 0) {
        if (decodedQueries[0].type === "player") {
          const playerQueries: PlayerSubQuery[] = decodedQueries
            .filter((q): q is StackedPlayerQuery => q.type === "player")
            .map((q) => {
              const availableSeasons = VideoSearchRanges[searchLeague]?.[q.seasonType] || VideoSearchRanges[searchLeague]["Regular Season"] || []
              const validSeasons = q.seasons.filter((s) => availableSeasons.includes(s))
              const querySeasons = validSeasons.length > 0 ? validSeasons : [availableSeasons[0] || (searchLeague === LEAGUE.WNBA ? ("2026" as Season) : ("2026-27" as Season))]

              const validStats = q.categories.filter((s) => Object.values(StatSchema.enum).includes(s))
              const queryStats = validStats.length > 0 ? validStats : [StatSchema.enum.find((v) => v === "PTS") || ("PTS" as Stat)]

              return {
                playerId: q.player.id,
                contexts: queryStats,
                seasons: querySeasons,
                seasonType: q.seasonType,
                opponentTeamId: q.oppTeamId && q.oppTeamId !== "0" ? q.oppTeamId : undefined,
                lastNGames: q.lastNGames && q.lastNGames !== "0" ? Number(q.lastNGames) : undefined,
                dateFrom: q.dateFrom || undefined,
                dateTo: q.dateTo || undefined,
                periods: q.periods || [],
              }
            })

          if (playerQueries.length > 0) {
            result = await getBatchPlayerPBPVideos(searchLeague, playerQueries, currentCursor, limit, order)
          }
        } else if (decodedQueries[0].type === "game") {
          const gameQueries: GameSubQuery[] = decodedQueries
            .filter((q): q is StackedGameQuery => q.type === "game")
            .map((q) => ({
              gameId: q.game.id,
              contexts: q.categories.length > 0 ? q.categories : undefined,
              seasonType: q.seasonType,
              players: q.players && q.players.length > 0 ? q.players.map((p) => p.id) : undefined,
              periods: q.periods || [],
            }))

          if (gameQueries.length > 0) {
            result = await getBatchGamePBPVideos(searchLeague, gameQueries, currentCursor, limit, order)
          }
        }
      }
    } else if (data.type === "player") {
      const parsedPeriods = data.periods
        ? data.periods.split(',').map((s) => Number(s.trim())).filter((n) => !isNaN(n) && n > 0)
        : []

      result = await getPlayerPBPVideos(
        searchLeague,
        data.player || '',
        data.lastN || '',
        data.stats && data.stats.length > 0 ? (data.stats.split(',') as Stat[]) : [],
        data.seasons && data.seasons.length > 0 ? (data.seasons.split(',') as Season[]) : [],
        data.seasonType as SeasonType,
        data.opponent || '',
        currentCursor,
        limit,
        data.dateFrom || '',
        data.dateTo || '',
        parsedPeriods,
        order
      )
    } else if (data.type === "game") {
      const rawPlayers = data.players || data.player
      const playerIds = rawPlayers
        ? rawPlayers.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined

      const parsedPeriods = data.periods
        ? data.periods.split(',').map((s) => Number(s.trim())).filter((n) => !isNaN(n) && n > 0)
        : []

      result = await getGamePBPVideos(
        data.game || '',
        searchLeague,
        data.seasonType as SeasonType,
        data.stats && data.stats.length > 0 ? (data.stats.split(',') as Stat[]) : [],
        currentCursor,
        limit,
        parsedPeriods,
        playerIds,
        order
      )
    }

    if (result && result.items) {
      const decodedFrom = decodeURIComponent(from || '')
      mergeSearchResults(
        searchLeague,
        decodedFrom,
        result.items,
        result.nextCursor ?? null,
        result.total ?? null,
        currentCursor === null,
        limit
      )
    }
  }, [from, games, mergeSearchResults, nbaPlayers, wnbaPlayers, isDesc])

  const loadClip = useCallback(async () => {
    const v = displayVideos.find((d) => d.eventNumber === parseInt(eventNumber) && parseInt(d.gameId) === parseInt(gameId))
    if (v) {
      setVideo(v)
      setClipLoading(false)
      setClipError(false)
      return
    }

    setClipLoading(true)
    setClipError(false)
    const res = await getClip(gameId, eventNumber)
    if (res) {
      setVideo(res)
      setClipError(false)
    } else {
      setClipError(true)
    }
    setClipLoading(false)
  }, [displayVideos, eventNumber, gameId])

  useEffect(() => {
    loadClip()
  }, [loadClip])

  // Scroll to top check
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setScrollIsVisible(true);
      } else {
        setScrollIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  useEffect(() => {
    if (from) {
      setActiveSearch(true)
      const data = Object.fromEntries(new URLSearchParams(decodeURIComponent(from))) as VideoSearchQueryData

      if (data.type !== "player" && data.type !== "game" && !data.q) {
        setSearchParams()
        return
      }

      const searchLeague = parseLeague(data.league)
      const decodedFrom = decodeURIComponent(from)
      const cached = searchCache[searchLeague]

      if (cached && cached.searchParamsString === decodedFrom && cached.videos.length > 0) {
        return
      }

      fetchData(data, location.state?.cursor || null)
    } else {
      setActiveSearch(false)
    }
  }, [from, fetchData, location.state?.cursor, searchCache, setSearchParams])

  // Handle select on video clip
  const handleOnSelect = async (video: VideoEvent) => {
    const hash = urlSafeBase64Encode(`${video.gameId}:${video.eventNumber}`)
    scrollToTop()
    const sortQuery = sortParam ? `&sort=${sortParam}` : ''
    navigate(`/video/${hash}?from=${encodeURIComponent(from || '')}${sortQuery}`, {
      replace: true
    })
  }

  const next = () => {
    if (!cursor || !from) return
    const data = new URLSearchParams(decodeURIComponent(from))
    fetchData(Object.fromEntries(data) as VideoSearchQueryData, cursor)
  }

  const handleOnBack = () => {
    if (from) {
      const data = new URLSearchParams(decodeURIComponent(from))
      navigate(`/search?${data.toString()}`, {
        replace: true
      })
    } else {
      navigate('/search', {
        replace: true
      })
    }
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <AppShell
      padding={"md"}
      header={{ height: 64, collapsed: !pinned }}
    >
      <NBAInsightsHeader />
      <AppShellMain>
        { clipLoading && <PageLoader mih="50vh" /> }
        { !clipLoading && clipError && (
          <Flex direction="column" align="center" justify="center" p={50} gap="md" mih="50vh">
            <Text fz="h3" fw={600} ta="center">Video clip unavailable</Text>
            <Text c="dimmed" size="sm" ta="center">
              The requested clip could not be loaded or may no longer exist.
            </Text>
            <Group mt="sm">
              <Button variant="default" onClick={loadClip}>
                Retry
              </Button>
              <Button variant="filled" onClick={handleOnBack}>
                Back to Search
              </Button>
            </Group>
          </Flex>
        )}
        { !clipLoading && !clipError && video && (
          <Box w={{ base: "100%", md: activeSearch ? "100%" : "80%" }} mx="auto">
            <FocusedVideoDisplay
              video={video}
              videos={displayVideos}
              activeSearch={activeSearch}
              onSelect={handleOnSelect}
              onBack={handleOnBack}
              total={total}
              onLoadMore={next}
              hasMore={hasMore && activeSearch}
              targetPlayerId={resolvedTargetPlayerId}
            />
          </Box>
        )}
          <Affix position={{ bottom: 30, right: "50%" }}>
          <Transition transition="slide-up" mounted={scrollIsVisible}>
            {(transitionStyles) => (
              <ActionIcon
                color="#5c5c5cff"
                size="lg"
                radius="xl"
                variant="filled"
                style={transitionStyles}
                onClick={scrollToTop}
                aria-label="Scroll to top"
              >
                <IconArrowUp color="black" size="1.125rem" />
              </ActionIcon>
            )}
          </Transition>
        </Affix>
      </AppShellMain>
    </AppShell>
  )
}

export default Video